/**
 * M10.2: browser session.
 *
 * The access token lives only in memory (never in localStorage, so an XSS cannot lift a
 * long-lived credential). The refresh token is an HttpOnly cookie the page cannot read;
 * `/auth/refresh` exchanges it (with the double-submit CSRF header) for a new access token.
 * Refreshes are single-flight within the tab and serialized across tabs (Web Locks), because
 * every refresh rotates the cookie.
 */
import type { AuthResponse } from '@galaxis/shared';

const LEGACY_TOKEN_KEY = 'galaxis_token';
const EARLY_REFRESH_MS = 60_000;

let accessToken: string | null = null;
let expiresAtMs = 0;
let inflight: Promise<AuthResponse | null> | null = null;
const logoutListeners = new Set<() => void>();

// Pre-M10.2 builds kept a never-expiring token in localStorage; it is useless now, drop it
try { localStorage.removeItem(LEGACY_TOKEN_KEY); } catch { /* storage unavailable */ }

export function csrfToken(): string {
  const m = document.cookie.match(/(?:^|;\s*)galaxis_csrf=([^;]+)/);
  return m ? decodeURIComponent(m[1]!) : '';
}

export function setSession(res: Pick<AuthResponse, 'token' | 'expiresAt'>): void {
  accessToken = res.token;
  expiresAtMs = Date.parse(res.expiresAt);
}

export function clearSession(): void {
  accessToken = null;
  expiresAtMs = 0;
}

export function hasSession(): boolean {
  return accessToken !== null;
}

/** Called when the server no longer accepts this browser's session (expired, revoked). */
export function onLoggedOut(fn: () => void): () => void {
  logoutListeners.add(fn);
  return () => { logoutListeners.delete(fn); };
}

export function notifyLoggedOut(): void {
  clearSession();
  for (const fn of logoutListeners) fn();
}

async function withRefreshLock<T>(fn: () => Promise<T>): Promise<T> {
  const locks = (navigator as Navigator & { locks?: { request(name: string, cb: () => Promise<T>): Promise<T> } }).locks;
  return locks ? locks.request('galaxis-auth-refresh', fn) : fn();
}

async function doRefresh(): Promise<AuthResponse | null> {
  return withRefreshLock(async () => {
    for (let attempt = 0; attempt < 3; attempt++) {
      const res = await fetch('/api/auth/refresh', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'X-CSRF-Token': csrfToken() },
      });
      if (res.status === 409) {
        // Another tab rotated the cookie a moment ago; the browser now holds the new one
        await new Promise(r => setTimeout(r, 150 * (attempt + 1)));
        continue;
      }
      if (!res.ok) return null;
      const body = await res.json() as AuthResponse;
      setSession(body);
      return body;
    }
    return null;
  });
}

/** Single-flight refresh; null = no valid session. */
export function refreshSession(): Promise<AuthResponse | null> {
  inflight ??= doRefresh().catch(() => null).finally(() => { inflight = null; });
  return inflight;
}

/** A valid access token, refreshing first when it is (nearly) expired. */
export async function getAccessToken(): Promise<string | null> {
  if (accessToken && Date.now() < expiresAtMs - EARLY_REFRESH_MS) return accessToken;
  const res = await refreshSession();
  return res?.token ?? null;
}
